import React from 'react';
import './TermsPage.scss';

export const TermsPage: React.FC = () => {
  return (
    <div className="terms-page">
      <div className="container">
        <div className="page-header">
          <h1>Terms & Conditions</h1>
          <p>Last updated: December 2024</p>
        </div>

        <div className="content">
          <section className="content-section">
            <h2>Acceptance of Terms</h2>
            <p>
              By accessing and using Odyssey, you accept and agree to be bound
              by these terms and conditions. If you do not agree to these terms,
              please do not use our service.
            </p>
          </section>

          <section className="content-section">
            <h2>Description of Service</h2>
            <p>
              Odyssey is an AI-powered educational platform that allows users to
              generate personalized courses, take lessons, complete quizzes, and
              track their learning progress. Our AI technology creates
              educational content based on your preferences and learning goals.
            </p>
            <p>
              <strong>Service Availability:</strong> We strive to maintain 99.9%
              uptime but cannot guarantee uninterrupted access. We may perform
              maintenance, updates, or experience technical issues that
              temporarily affect service availability.
            </p>
          </section>

          <section className="content-section">
            <h2>User Accounts and Responsibilities</h2>
            <h3>Account Security</h3>
            <p>
              You are responsible for maintaining the confidentiality of your
              account credentials and for all activities under your account.
              Notify us immediately of any unauthorized access.
            </p>
            <h3>Accurate Information</h3>
            <p>
              You must provide accurate, current, and complete information
              during registration and keep your account information updated.
            </p>
            <h3>Age Requirements</h3>
            <p>
              You must be at least 13 years old to use our service. Users under
              18 should have parental consent.
            </p>
          </section>

          <section className="content-section">
            <h2>AI-Generated Content and Intellectual Property</h2>
            <h3>Your Content</h3>
            <p>
              You retain ownership of any original content you submit to our
              platform. By using our service, you grant us a license to use your
              input to generate educational content and improve our AI models.
            </p>
            <h3>AI-Generated Content</h3>
            <p>
              Courses and materials generated by our AI become available for
              your use under these terms. While you can use this content for
              your personal learning, we retain rights to the underlying AI
              technology and may provide similar content to other users.
            </p>
            <h3>Content Accuracy</h3>
            <p>
              AI-generated educational content is provided for informational
              purposes. While we strive for accuracy, you should verify
              important information from authoritative sources. We are not
              responsible for decisions made based solely on AI-generated
              content.
            </p>
          </section>

          <section className="content-section">
            <h2>Prohibited Uses</h2>
            <p>You may not use our service to:</p>
            <p>• Create content that violates intellectual property rights</p>
            <p>• Generate harmful, illegal, or offensive material</p>
            <p>• Attempt to reverse engineer our AI models</p>
            <p>• Use automated tools to access our service excessively</p>
            <p>• Share your account credentials with others</p>
            <p>
              • Use the service for commercial purposes without authorization
            </p>
          </section>

          <section className="content-section">
            <h2>Payment Terms and Subscriptions</h2>
            <h3>Billing</h3>
            <p>
              Subscription fees are billed in advance on a recurring basis. All
              payments are non-refundable except as required by law.
            </p>
            <h3>Plan Changes</h3>
            <p>
              You may upgrade, downgrade, or cancel your subscription at any
              time. Changes take effect at the next billing cycle.
            </p>
            <h3>Free Trial</h3>
            <p>
              Free trials automatically convert to paid subscriptions unless
              canceled before the trial period ends.
            </p>
          </section>

          <section className="content-section">
            <h2>Privacy and Data Use</h2>
            <p>
              Your privacy is important to us. Our use of your personal
              information is governed by our Privacy Policy. By using our
              service, you consent to the collection and use of your information
              as described in our Privacy Policy.
            </p>
            <p>
              We may use anonymized usage data to improve our AI models and
              service quality.
            </p>
          </section>

          <section className="content-section">
            <h2>Educational Use and Limitations</h2>
            <p>
              <strong>Supplementary Tool:</strong> Odyssey is designed to
              supplement, not replace, traditional education. Our content should
              not be your sole source of learning for critical subjects.
            </p>
            <p>
              <strong>Professional Advice:</strong> Our content does not
              constitute professional, medical, legal, or financial advice.
              Consult qualified professionals for such matters.
            </p>
            <p>
              <strong>Certification:</strong> Unless explicitly stated, our
              courses do not provide accredited certifications or professional
              credentials.
            </p>
          </section>

          <section className="content-section">
            <h2>Limitation of Liability</h2>
            <p>
              Odyssey and its affiliates shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages, including
              loss of profits, data, or use, even if we have been advised of the
              possibility of such damages.
            </p>
            <p>
              Our total liability shall not exceed the amount you paid for the
              service in the 12 months preceding the claim.
            </p>
          </section>

          <section className="content-section">
            <h2>Service Modifications and Termination</h2>
            <h3>Our Right to Modify</h3>
            <p>
              We may modify, suspend, or discontinue any aspect of our service
              at any time with reasonable notice.
            </p>
            <h3>Account Termination</h3>
            <p>
              We may suspend or terminate your account for violations of these
              terms, illegal activity, or abuse of our service. You may cancel
              your account at any time through your account settings.
            </p>
            <h3>Effect of Termination</h3>
            <p>
              Upon termination, your access to the service ends, but these terms
              continue to apply to both parties as applicable.
            </p>
          </section>

          <section className="content-section">
            <h2>Dispute Resolution</h2>
            <p>
              Any disputes arising from these terms or your use of our service
              shall be resolved through binding arbitration rather than court
              proceedings, except where prohibited by law.
            </p>
          </section>

          <section className="content-section">
            <h2>Changes to Terms</h2>
            <p>
              We may update these terms periodically. Significant changes will
              be communicated via email or platform notification. Continued use
              after changes indicates acceptance of the updated terms.
            </p>
          </section>

          <section className="content-section">
            <h2>Contact Information</h2>
            <p>For questions about these Terms & Conditions, contact us at:</p>
            <p>
              <strong>Email:</strong> legal@aiuniversity.com
            </p>
            <p>
              <strong>General inquiries:</strong> hello@aiuniversity.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};
